import Loader from '../Loader/Loader';

/**
 * Button component with customizable color, size, and children and it also takes default button props like type, onClick etc
 * @param {Object} props - The component props.
 * @param {"primary"|"success"|"error"} [props.color] - The background color of the button. Defaults to "gray" if not specified.
 * @param {"sm"} [props.size] - The size of the button. "sm" for small size default it will be "md".
 * @param {boolean} [props.loading] - boolean value that make button loading effect default value is false
 * @param {React.ReactNode} [props.startIcon] - it is the icon thats come before the label
 * @param {React.ReactNode} props.children - JSX element that will render button label
 * @returns {JSX.Element} The rendered button component.
 */
const Button: React.FC<IButtonProps> = ({
  color,
  size = 'md',
  startIcon,
  children,
  loading = false,
  widthFull = false,
  disabled,
  onClick,
  className: customClassName,
  ...btnprops
}) => {
  let className = `text-nowrap flex items-center justify-center py-3 px-5 rounded-lg overflow-hidden`;

  // Add color-specific classes
  switch (color) {
    case 'primary':
      className +=
        ' bg-blue-100 hover:bg-ds-mc-blue-400 hover:border-2 hover:border-ds-mc-blue-400 text-[#ffffff]';
      break;
    case 'success':
      className +=
        ' bg-ds-green-500 hover:bg-ds-green-400 hover:border-2 hover:border-ds-green-400 text-white';
      break;
    case 'error':
      className +=
        ' bg-red-500 hover:bg-red-600 hover:border-2 hover:border-red-600 font-semibold text-white';
      break;
    case 'warning':
      className +=
        ' bg-ds-brand-mc-orange-2 hover:bg-ds-brand-mc-orange-1 hover:border-2 hover:border-ds-brand-mc-orange-1 font-semibold text-white';
      break;
    case 'outlined':
      className +=
        ' bg-primary-200 hover:bg-ds-mc-blue-100 hover:border-2 hover:border-ds-mc-blue-100 text-[#1F2A37] border-2 border-ds-gray-100';
      break;
    case 'gray':
      className += ' bg-ds-gray-300 hover:bg-gray-200 text-[#FFFFFF] ';
      break;
    case 'secondary':
      className += ' bg-[#973F98] hover:bg-[#b86bb7] text-[#FFFFFF] ';
      break;
    default:
      className += ' bg-[#FFFFFF] text-[#6E6E6E] border border-gray-200 border-1';
      break;
  }

  if (disabled) {
    className += ' cursor-not-allowed opacity-50 text-white';
  }

  if (widthFull) {
    className += ' w-full h-10 text-sm';
  }
  if (!widthFull) {
    switch (size) {
      case 'xs':
        className += ' text-xs w-[30px] h-10';
        break;
      case 'sm':
        className += ' text-sm w-[72px] h-[40px]';
        break;
      case 'md':
        className += ' w-32 h-10 text-sm';
        break;
      case 'lg':
        className += ' w-40 h-10 text-sm';
        break;
      case 'xl':
        className += ' w-48 h-10 text-sm';
        break;
      default:
        className += ' h-10 text-sm';
        break;
    }
  }

  if (customClassName) {
    className += ` ${customClassName}`;
  }

  return (
    <>
      <div className="relative" id="dropdown">
        <button
          className={className}
          onClick={(e) => {
            if (disabled) return;
            onClick && onClick(e);
          }}
          disabled={disabled || loading}
          {...btnprops}
        >
          {startIcon && <span className="flex  items-center mr-2">{startIcon}</span>}
          <span className={`${loading ? 'mr-2' : ''}`}>{children}</span>
          <div className="flex items-center justify-center">
            {loading && (
              <Loader
                size="md"
                color={
                  color === 'primary'
                    ? 'primary'
                    : color === 'success'
                      ? 'success'
                      : color === 'error'
                        ? 'error'
                        : 'default'
                }
              />
            )}
          </div>
        </button>
      </div>
    </>
  );
};

interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  color?: 'primary' | 'success' | 'error' | 'outlined' | 'warning' | 'gray' | 'secondary';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children?: React.ReactNode;
  startIcon?: React.ReactNode;
  loading?: boolean;
  widthFull?: boolean;
  isOpen?: boolean;
  className?: string;
}

export default Button;
